import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import { bucketKey, formatBucket, granularityForSpan } from '@lib/charts/timeBuckets';
import { useTheme } from '@mui/material';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { type DetailedPOI } from './Detail';

interface OverTimeChartProps {
  pois: DetailedPOI[];
}

interface ChartDataPoint {
  date: string;
  ok: number;
  warning: number;
  error: number;
  notChecked: number;
}

type StatusKey = Exclude<keyof ChartDataPoint, 'date'>;

const OvertimeChart = ({ pois }: OverTimeChartProps) => {
  const { t, currentLanguage } = useLocalization();
  const noticeColors = useNoticeColors();
  const theme = useTheme();

  const { chartData, granularity } = useMemo(() => {
    if (!pois || pois.length === 0) return { chartData: [] as ChartDataPoint[], granularity: 'day' as const };

    const granularity = granularityForSpan(pois.map(poi => new Date(poi.info.dateModified)));
    const dateMap = new Map<string, ChartDataPoint>();

    pois.forEach((poi) => {
      const step = poi.entry.steps[poi.entry.stepIndex];
      if (!step) return;

      const dateKey = bucketKey(new Date(poi.info.dateModified), granularity);

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {
          date: dateKey,
          ok: 0,
          warning: 0,
          error: 0,
          notChecked: 0,
        });
      }

      const dataPoint = dateMap.get(dateKey)!;

      const status = SPInspection.getPOIStatus(poi.entry);

      if (status === 'Ok') dataPoint.ok++;
      else if (status === 'Error') dataPoint.error++;
      else if (status === 'Warning') dataPoint.warning++;
      else dataPoint.notChecked++;
    });

    const result = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));

    return { chartData: result, granularity };
  }, [pois]);

  const series: { key: StatusKey, name: string, color: string }[] = [
    { key: 'ok', name: t('Common_Successful'), color: noticeColors.Ok },
    { key: 'warning', name: t('Common_Warning'), color: noticeColors.Warning },
    { key: 'error', name: t('Common_Error'), color: noticeColors.Error },
    { key: 'notChecked', name: t('Common_NotChecked'), color: noticeColors.NotChecked },
  ];

  // A status that never occurs would only add an empty slot to every group.
  const activeSeries = series.filter(s => chartData.some(point => point[s.key] > 0));

  // The counts are small integers, so label the bars directly while the axis has room for it.
  const showValueLabels = chartData.length <= 12;

  const formatDate = (dateStr: string) => formatBucket(dateStr, granularity, currentLanguage);

  return (
    <ResponsiveContainer width="100%" height="100%" style={{ marginTop: '36px' }}>
      <BarChart
        data={chartData}
        margin={{ top: 16, right: 30, left: 20, bottom: 5 }}
        barGap={2}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={false}
          tickLine={false}
          tickFormatter={formatDate}
        />
        <YAxis axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip
          cursor={{ fill: theme.palette.action.hover }}
          labelFormatter={formatDate}
          contentStyle={{
            backgroundColor: '#1E1E2D',
            border: '1px solid #333',
            borderRadius: 8,
            color: '#F1F1F2',
            fontSize: 12,
          }}
          labelStyle={{
            color: '#FFFFFF',
            fontWeight: 'bold',
          }}
          itemStyle={{
            color: '#E1E1E2',
          }}
        />
        {activeSeries.map(({ key, name, color }) => (
          <Bar key={key} dataKey={key} name={name} fill={color} radius={[4, 4, 0, 0]} maxBarSize={32}>
            {showValueLabels && (
              <LabelList
                dataKey={key}
                position="top"
                fontSize={11}
                fill={theme.palette.text.secondary}
                formatter={(value: number) => (value > 0 ? value : '')}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default OvertimeChart;
